import {FC, useState} from "react";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {motion, AnimatePresence} from "framer-motion";
import {
    HomeIcon,
    CubeIcon,
    ShoppingBagIcon,
    ChartBarIcon,
    Cog6ToothIcon,
    ArrowRightOnRectangleIcon,
} from "@heroicons/react/24/outline";

interface SideMenuProps {
}

const SideMenu: FC<SideMenuProps> = () => {
    const {pathname} = useLocation();
    const navigate = useNavigate();
    const [hoveredItem, setHoveredItem] = useState<string | null>(null);

    function logout() {
        // Remove access token from localStorage
        localStorage.removeItem('accessToken');

        // Redirect to home page
        navigate('/');
    }

    const menuItems = [
        {path: "/dashboard", icon: HomeIcon, label: "Dashboard", tooltip: "Dashboard"},
        {path: "/architecture", icon: CubeIcon, label: "Projects", tooltip: "Projects"},
        {path: "/marketplace", icon: ShoppingBagIcon, label: "Marketplace", tooltip: "Marketplace"},
        {path: "/analytics", icon: ChartBarIcon, label: "Analytics", tooltip: "Analytics"}
    ];

    const isActive = (path: string) => pathname?.includes(path.slice(1));

    return (
        <motion.div
            id="sidebar"
            initial={{x: -100, opacity: 0}}
            animate={{x: 0, opacity: 1}}
            transition={{duration: 0.5, ease: "easeOut"}}
            className="
        backdrop-blur-xl bg-gradient-to-b from-slate-900/95 via-slate-800/95 to-slate-900/95
        border border-slate-700/50 shadow-2xl shadow-indigo-500/20
        rounded-2xl h-full py-4 flex flex-col
        w-full transition-all duration-1000 ease-out
        hover:shadow-indigo-500/30 overflow-hidden
      "
        >
            {/* Background Gradient Overlay */}
            <div
                className="absolute inset-0 bg-gradient-to-br from-indigo-600/10 via-purple-600/10 to-pink-600/10 rounded-2xl"/>

            {/* AI Particles Effect */}
            <div className="absolute inset-0 overflow-hidden rounded-2xl">
                {[...Array(8)].map((_, i) => (
                    <motion.div
                        key={i}
                        className="absolute w-1 h-1 bg-indigo-400/60 rounded-full"
                        animate={{
                            x: [0, 60, 0],
                            y: [0, -40, 0],
                            opacity: [0.3, 0.8, 0.3]
                        }}
                        transition={{
                            duration: 3 + i,
                            repeat: Infinity,
                            delay: i * 0.5
                        }}
                        style={{
                            left: `${15 + i * 8}%`,
                            top: `${20 + i * 10}%`
                        }}
                    />
                ))}
            </div>

            {/* Logo Section - Fixed at top */}
            <motion.div
                className="text-center mb-6 px-6 flex-shrink-0 relative z-10"
                whileHover={{scale: 1.05}}
                transition={{type: "spring", stiffness: 400}}
            >
                <div className="relative inline-block">
                    {/* Enhanced Logo Particles - Light Blue, Dark Blue, and Gold */}
                    <div className="absolute inset-0 overflow-visible">
                        {/* Light Blue Particles */}
                        {[...Array(6)].map((_, i) => (
                            <motion.div
                                key={`light-blue-${i}`}
                                className="absolute w-1.5 h-1.5 bg-sky-400/80 rounded-full shadow-lg shadow-sky-400/50"
                                animate={{
                                    x: [0, 25 * Math.cos(i * Math.PI / 3), 0],
                                    y: [0, 25 * Math.sin(i * Math.PI / 3), 0],
                                    opacity: [0.4, 1, 0.4],
                                    scale: [0.8, 1.2, 0.8]
                                }}
                                transition={{
                                    duration: 4 + i * 0.5,
                                    repeat: Infinity,
                                    delay: i * 0.3,
                                    ease: "easeInOut"
                                }}
                                style={{
                                    left: '50%',
                                    top: '50%',
                                    transform: 'translate(-50%, -50%)'
                                }}
                            />
                        ))}

                        {/* Dark Blue Particles */}
                        {[...Array(8)].map((_, i) => (
                            <motion.div
                                key={`dark-blue-${i}`}
                                className="absolute w-1 h-1 bg-blue-700/90 rounded-full shadow-md shadow-blue-700/60"
                                animate={{
                                    x: [0, 35 * Math.cos((i * Math.PI / 4) + Math.PI / 8), 0],
                                    y: [0, 35 * Math.sin((i * Math.PI / 4) + Math.PI / 8), 0],
                                    opacity: [0.3, 0.9, 0.3],
                                    rotate: [0, 360, 0]
                                }}
                                transition={{
                                    duration: 5 + i * 0.3,
                                    repeat: Infinity,
                                    delay: i * 0.4,
                                    ease: "linear"
                                }}
                                style={{
                                    left: '50%',
                                    top: '50%',
                                    transform: 'translate(-50%, -50%)'
                                }}
                            />
                        ))}

                        {/* Gold Particles */}
                        {[...Array(4)].map((_, i) => (
                            <motion.div
                                key={`gold-${i}`}
                                className="absolute w-2 h-2 bg-yellow-700/95 rounded-full shadow-lg shadow-yellow-400/70"
                                animate={{
                                    x: [0, 45 * Math.cos(i * Math.PI / 2), 0],
                                    y: [0, 45 * Math.sin(i * Math.PI / 2), 0],
                                    opacity: [0.5, 1, 0.5],
                                    scale: [0.6, 1.4, 0.6]
                                }}
                                transition={{
                                    duration: 6 + i * 0.8,
                                    repeat: Infinity,
                                    delay: i * 0.6,
                                    ease: "easeInOut"
                                }}
                                style={{
                                    left: '50%',
                                    top: '50%',
                                    transform: 'translate(-50%, -50%)'
                                }}
                            />
                        ))}

                        {/* Orbiting Particles */}
                        {[...Array(12)].map((_, i) => (
                            <motion.div
                                key={`orbit-${i}`}
                                className={`absolute w-0.5 h-0.5 rounded-full ${
                                    i % 3 === 0 ? 'bg-sky-300/70' : 
                                    i % 3 === 1 ? 'bg-blue-600/70' : 
                                    'bg-yellow-300/70'
                                }`}
                                animate={{
                                    rotate: [0, 360]
                                }}
                                transition={{
                                    duration: 8 + (i % 4),
                                    repeat: Infinity,
                                    ease: "linear",
                                    delay: i * 0.2
                                }}
                                style={{
                                    left: '50%',
                                    top: '50%',
                                    transformOrigin: `${30 + (i % 3) * 5}px center`
                                }}
                            />
                        ))}
                    </div>
                    
                    <img
                        src="/images/logo2.png"
                        alt="Logo"
                        className="h-20 w-20 object-contain mx-auto mb-3 relative z-10"
                    />
                </div>
                <div className="relative inline-block">
                    <h1
                        className="text-2xl font-bold bg-gradient-to-r from-white via-indigo-200 to-purple-200 bg-clip-text text-transparent">
                        Shreds
                    </h1>
                </div>
            </motion.div>

            {/* Scrollable Content Area */}
            <div className="flex-1 flex flex-col overflow-y-auto relative z-10 min-h-0">
                {/* User Profile */}
                <motion.div
                    className="mx-6 mb-6 p-4 rounded-xl bg-gradient-to-br from-slate-800/50 to-slate-700/50 border border-slate-600/30 flex-shrink-0"
                    whileHover={{scale: 1.02, backgroundColor: "rgba(51, 65, 85, 0.6)"}}
                    transition={{duration: 0.2}}
                >
                    <div className="flex flex-col items-center text-center">
                        <div className="relative mb-2">
                            <motion.img
                                className="w-10 h-10 rounded-full border-2 border-indigo-400/50 shadow-lg"
                                src="/images/user-default.jpg"
                                alt="user"
                                whileHover={{scale: 1.1}}
                                transition={{type: "spring", stiffness: 400}}
                            />
                            <div
                                className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-slate-800"/>
                        </div>
                        <h3 className="text-sm font-semibold text-white">Architect</h3>
                    </div>
                    <motion.button
                        onClick={logout}
                        className="flex items-center justify-center gap-2 w-full mt-3 px-3 py-2 text-xs text-slate-300 hover:text-white
                     bg-slate-700/50 hover:bg-red-500/20 rounded-lg transition-all duration-200
                     border border-slate-600/30 hover:border-red-500/30"
                        whileHover={{scale: 1.02}}
                        whileTap={{scale: 0.98}}
                    >
                        <ArrowRightOnRectangleIcon className="h-4 w-4"/>
                        <span>Logout</span>
                    </motion.button>
                </motion.div>

                {/* Navigation Menu */}
                <nav className="flex-1 flex flex-col gap-2 px-6 pb-4">
                    {menuItems.map((item, index) => (
                        <motion.div
                            key={item.path}
                            initial={{opacity: 0, x: -20}}
                            animate={{opacity: 1, x: 0}}
                            transition={{delay: index * 0.1}}
                            onMouseEnter={() => setHoveredItem(item.path)}
                            onMouseLeave={() => setHoveredItem(null)}
                        >
                            <Link
                                to={item.path}
                                className={`
                  relative flex items-center gap-3 p-3 rounded-xl transition-all duration-300
                  group hover:bg-indigo-600/20 hover:shadow-lg hover:shadow-indigo-500/20
                  ${isActive(item.path)
                                    ? "bg-gradient-to-r from-indigo-600/30 to-purple-600/30 shadow-lg shadow-indigo-500/20 border border-indigo-500/30"
                                    : "hover:border-0 hover:border-indigo-500/20"
                                }
                `}
                            >
                                {/* Active indicator */}
                                <AnimatePresence>
                                    {isActive(item.path) && (
                                        <motion.div
                                            initial={{scaleY: 0}}
                                            animate={{scaleY: 1}}
                                            exit={{scaleY: 0}}
                                            className="absolute left-0 top-2 bottom-2 w-1 bg-gradient-to-b from-indigo-400 to-purple-400 rounded-r-full"
                                        />
                                    )}
                                </AnimatePresence>

                                {/* Icon */}
                                <motion.div
                                    whileHover={{scale: 1.1, rotate: 5}}
                                    whileTap={{scale: 0.9}}
                                    transition={{type: "spring", stiffness: 400}}
                                >
                                    <item.icon
                                        className={`
                      h-5 w-5 transition-colors duration-200
                      ${isActive(item.path)
                                            ? "text-white"
                                            : "text-slate-400 group-hover:text-white"
                                        }
                    `}
                                    />
                                </motion.div>

                                {/* Label */}
                                <span className={`
                  text-sm font-medium transition-colors duration-200
                  ${isActive(item.path)
                                    ? "text-white"
                                    : "text-slate-300 group-hover:text-white"
                                }
                `}>
                  {item.label}
                </span>

                                {/* Hover glow effect */}
                                <AnimatePresence>
                                    {hoveredItem === item.path && (
                                        <motion.div
                                            initial={{opacity: 0, scale: 0.8}}
                                            animate={{opacity: 1, scale: 1}}
                                            exit={{opacity: 0, scale: 0.8}}
                                            className="absolute inset-0 bg-gradient-to-r from-indigo-600/10 to-purple-600/10 rounded-xl"
                                        />
                                    )}
                                </AnimatePresence>
                            </Link>
                        </motion.div>
                    ))}
                </nav>
            </div>

            {/* Settings at bottom - Fixed */}
            <div className="relative z-10 px-6 pt-4 flex-shrink-0 border-t border-slate-700/30">
                <motion.div
                    whileHover={{scale: 1.02}}
                    whileTap={{scale: 0.98}}
                >
                    <Link
                        to="/settings"
                        className={`
              relative flex items-center gap-3 p-3 rounded-xl transition-all duration-300
              group hover:bg-slate-700/50 hover:shadow-lg w-full
              ${isActive("/settings")
                            ? "bg-gradient-to-r from-slate-700/50 to-slate-600/50 shadow-lg border border-slate-500/30"
                            : "hover:border-0 hover:border-slate-500/20"
                        }
            `}
                    >
                        <AnimatePresence>
                            {isActive("/settings") && (
                                <motion.div
                                    initial={{scaleY: 0}}
                                    animate={{scaleY: 1}}
                                    exit={{scaleY: 0}}
                                    className="absolute left-0 top-2 bottom-2 w-1 bg-slate-400 rounded-r-full"
                                />
                            )}
                        </AnimatePresence>

                        <motion.div
                            whileHover={{rotate: 90}}
                            transition={{duration: 0.3}}
                        >
                            <Cog6ToothIcon
                                className={`
                  h-5 w-5 transition-colors duration-200
                  ${isActive("/settings")
                                    ? "text-white"
                                    : "text-slate-400 group-hover:text-white"
                                }
                `}
                            />
                        </motion.div>

                        <span className={`
              text-sm font-medium transition-colors duration-200
              ${isActive("/settings")
                            ? "text-white"
                            : "text-slate-300 group-hover:text-white"
                        }
            `}>
              Settings
            </span>
                    </Link>
                </motion.div>
            </div>
        </motion.div>
    );
};

export default SideMenu;